/**
 * runLabels — plain-English names for the pass/run codes founders see.
 *
 * The Dashboard "Live Runs" table and an agent's "Recent runs" table render a
 * run's raw `pass` code (e.g. `analytics-ingest`, `funnel-health`,
 * `costs-rollup`). Those are engineer slugs, not something a non-technical
 * founder can read. `passLabel` maps every known pass to one short noun phrase
 * that says what the agent actually did; the raw code stays reachable as the
 * cell's `title` tooltip. Anything unmapped falls back to a de-slugged
 * Title-case string, so a new handler never leaks a raw `foo-bar` slug.
 *
 * Kept dependency-free so it is unit-tested directly (tests/run-labels.test.ts).
 */

/**
 * pass code → what the agent did, in words. Wording matches the rest of HQ
 * (e.g. the Feed already calls a `standup` a "daily check-in").
 */
const PASS_LABELS: Record<string, string> = {
  // scheduled/system passes that show up in prod today
  standup: 'Daily check-in',
  'analytics-ingest': 'Sales-number pull',
  'funnel-health': 'Funnel health check',
  'costs-rollup': 'Daily spend tally',

  // approval machinery
  'execute-approval': 'Ran an approved action',
  'post-execution-verify': 'Post-run double-check',

  // the rest of the runner's handlers
  'campaign-build': 'Email build',
  'calendar-pass': 'Send-calendar plan',
  'flow-build': 'Automation build',
  'cro-backlog': 'Page-idea backlog',
  'sandbox-funnel': 'Test-funnel build',
  'creative-review': 'Creative review',
  'knowledge-ingest': 'Knowledge filing',
  'retire-node': 'Knowledge cleanup',
  'brand-patch': 'Brand-guide update',
  'hq-selftest': 'HQ self-check',
  archaeology: 'Funnel history dig',
  retro: 'Team retro',
  chat: 'Chat',
  'card-qa': 'Work-card QA',
  'card-respond': 'Card reply',
  'card-mention': 'Mention pickup',
  'card-pickup': 'Card pickup',
  'revise-card': 'Card revision',

  // agent-prefixed pass names from the src/passes registry (`npm run pass`)
  'agent-retro': 'Team retro',
  'iris-review': 'Creative review',
  'leo-archaeology': 'Funnel history dig',
  'leo-cro-backlog': 'Page-idea backlog',
  'leo-sandbox-funnel': 'Test-funnel build',
  'milo-calendar': 'Send-calendar plan',
  'milo-campaign': 'Email build',
  'milo-campaign-v2': 'Email build',
  'milo-flow-build': 'Automation build',
  'quinn-gauntlet': 'Full QA gauntlet',
  'quinn-hq-selftest': 'HQ self-check',
  preflight: 'Preflight check',
  smoke: 'Smoke test',
};

/** De-slug an unknown pass code: 'foo-bar_baz' → 'Foo bar baz'. */
function deslug(pass: string): string {
  const words = pass.replace(/[-_]+/g, ' ').trim();
  if (!words) return pass;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Plain-English name for a run's `pass` code. Unknown codes get a de-slugged
 * Title-case fallback so a raw slug never reaches a founder.
 */
export function passLabel(pass: string | null | undefined): string {
  if (!pass) return 'Work';
  const key = pass.trim();
  return PASS_LABELS[key] ?? deslug(key);
}
